import React, { useEffect, useState } from 'react'

import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'

import QueryLoader from '~/components/QueryLoader'
import { StatsCard, StatsCardInfoTooltip } from '~/components/stats/StatsComponents'
import { useLpiSummary } from '~/components/stats/useLpiSummary'
import SkeletonLoader from '~/components/ui/SkeletonLoader'
import useTrackClick from '~/events/trackers/useTrackClick'
import { useCommonTx } from '~/hooks/useTranslationForNamespace'
import Clock from '~/images/icons/Raw/Clock.svg'
import ArrowRight from '~/images/icons/SystemOutlined/ArrowRight.svg'
import ChevronRight from '~/images/icons/SystemOutlined/ChevronRight.svg'
import CloseIcon from '~/images/icons/SystemOutlined/Close.svg'
import { ThunderIcon } from '~/images/stats/ThunderIcon'
import { Button } from '~/ui/Button/Button'
import { ButtonCounter } from '~/ui/Button/ButtonCounter'
import { SliderResponsive } from '~/ui/Drawer/SliderResponsive'

/**
 *
 * for now all layouts are mostly same, except few changes.
 * there might be drastic change is these layouts in future,
 * the core ui rendering will mostly be same
 */
export const LpiStatCard = ({ layoutName }: { layoutName: 'A' | 'B' | 'C' }) => {
  const { loading, error, hasAreaWithNoLpi, lpiByArea, overallLpi, overallLpiText, overallPercent } = useLpiSummary()

  const { trackCta } = useTrackClick()
  const router = useRouter()
  const t = useCommonTx('stats')
  const ct = useCommonTx('common')

  const onListClick = ({ areaSlug }: { areaSlug: string }) => {
    trackCta({
      type: 'button',
      click_name: 'stats_lpi_card_area_click',
      destination: 'game_lpi_rankings',
      text: ct(`areas.${areaSlug}.name`),
      value: areaSlug,
    })
    router.push(`/training-areas?area_slug=${areaSlug}`)
  }

  const onOverallLpiClick = () => {
    router.push(`/training-areas`)
  }

  const isLayoutA = layoutName === 'A' // used in stats page
  const isLayoutC = layoutName === 'C' // used in drawer
  // const isLayoutB = layoutName === 'B' // used in home page & games page

  const qlSkeleton = <SkeletonLoader className='rounded-lg min-h-[300px] w-full' />
  const qlData = !loading && lpiByArea
  const qlLoading = loading
  const qlError = error

  const content = (
    <div className='flex flex-col w-full'>
      <OverallLpiSection
        className='py-5 pl-6 pr-5'
        progress={overallPercent}
        progressValue={overallLpiText}
        title={t('card.lpi.overallTitle')}
        emptyText={
          hasAreaWithNoLpi
            ? t('card.lpi.hasAreaWithNoLpiText')
            : overallLpi <= 0
            ? t('card.lpi.initialText')
            : undefined
        }
        onClick={isLayoutA ? () => onOverallLpiClick() : undefined}
      />

      <div className='pl-12 pb-2 flex flex-col w-full'>
        {lpiByArea.map((item) => {
          return (
            <AreaItem
              key={item.areaSlug}
              className='border-t border-border-subdued pr-5 py-3'
              title={ct(`areas.${item.areaSlug}.name`)}
              titleInfo={item.notIncludedInLPI ? t('notIncludedInLPI') : undefined}
              progress={item.relativeProgress}
              progressValue={item.lpi === 0 ? <div className='text-text-disabled'>--</div> : <div>{item.lpiText}</div>}
              onClick={isLayoutA ? () => onListClick({ areaSlug: item.areaSlug }) : undefined}
            />
          )
        })}
      </div>
    </div>
  )

  if (isLayoutC) {
    return (
      <QueryLoader loading={qlLoading} error={qlError} data={qlData} LoadingComponent={qlSkeleton}>
        {content}
      </QueryLoader>
    )
  }

  if (isLayoutA) {
    return (
      <QueryLoader loading={qlLoading} error={qlError} data={qlData} LoadingComponent={qlSkeleton}>
        <StatsCard title={<CardTitle title={ct('lpiText')} />} titleAction={<WhatIsLpi />}>
          {content}
        </StatsCard>
      </QueryLoader>
    )
  }

  return (
    <QueryLoader loading={qlLoading} error={qlError} data={qlData} LoadingComponent={qlSkeleton}>
      <StatsCard title={<CardTitle title={ct('currentLpi')} />} footerAction={<MoreInStats kind='tertiary' />}>
        {content}
      </StatsCard>
    </QueryLoader>
  )
}

export type ProgressCompletionBarProps = {
  progress: number // percentage complete
  value: React.ReactNode
  className?: string
}
export const ProgressBarContainer = ({ progress, value, className }: ProgressCompletionBarProps) => {
  const [progressWidth, setProgressWidth] = React.useState(0)

  useEffect(() => {
    setProgressWidth(progress)
  }, [progress])

  return (
    // we have to put width at the parent div, because the progress bar's 100% will consider the value section as well,
    // this there will be no effect post 88% to 100%, (here 88% is based on width of container div)
    <div
      className={clsx('flex flex-row items-center gap-2 font-normal transition-all duration-700', className)}
      style={{
        width: progress ? `${progressWidth}%` : undefined,
      }}
    >
      <div
        data-name='progress'
        className={clsx(progress ? 'w-full' : 'w-1', 'min-w-1 rounded bg-yellow-gradient-2 h-[18px]')}
      />
      <div className='whitespace-nowrap'>{value}</div>
    </div>
  )
}

const OverallLpiSection = ({
  title,
  emptyText,
  progress,
  progressValue,
  className,
  onClick,
}: {
  title: React.ReactNode
  emptyText?: React.ReactNode
  progress: number
  progressValue: React.ReactNode
  className?: string
  onClick?: () => void
}) => {
  return (
    <button
      className={clsx(
        'flex flex-row justify-center items-center gap-4 font-normal w-full',
        !onClick && 'cursor-default',
        className,
      )}
      onClick={() => onClick?.()}
    >
      <div className='flex flex-col justify-center w-full font-normal flex-1'>
        <div className='flex flex-row justify-between mb-1 text-body-text-2 font-semibold'>{title}</div>
        {emptyText && <div className='text-caption-1 text-text-subdued text-left'>{emptyText}</div>}
        {!emptyText && <ProgressBarContainer progress={progress} value={progressValue} className='text-heading-3' />}
      </div>
      {onClick && <ChevronRight title='t' className='block min-w-7 min-h-7 text-icon-disabled' />}
    </button>
  )
}

const AreaItem = ({
  title,
  titleInfo,
  progress,
  progressValue,
  className,
  onClick,
}: {
  progress: number
  title: React.ReactNode
  titleInfo?: React.ReactNode
  progressValue: React.ReactNode
  className?: string
  onClick?: () => void
}) => {
  return (
    <button
      className={clsx(
        'flex flex-row justify-center items-center gap-4 font-normal',
        !onClick && 'cursor-default',
        className,
      )}
      onClick={() => onClick?.()}
    >
      <div className='flex flex-col w-full'>
        <div className='flex flex-row justify-between items-center mb-1 font-normal text-body-text-3'>
          <div className='flex flex-row items-baseline flex-wrap gap-1'>
            {title} {titleInfo && <div className='text-caption-2 text-text-subdued'>{titleInfo}</div>}
          </div>
        </div>
        <ProgressBarContainer progress={progress} value={progressValue} className='text-body-text-3' />
      </div>
      {onClick && <ChevronRight title='t' className='block min-w-7 min-h-7 text-icon-disabled' />}
    </button>
  )
}

const CardTitle = ({ title }: { title: React.ReactNode }) => {
  return (
    <div className='flex flex-row items-center gap-2'>
      <ThunderIcon className='w-4 h-6' />
      {title}
    </div>
  )
}

const WhatIsLpi = () => {
  const t = useCommonTx('stats.card.lpi')
  const { trackCta } = useTrackClick()

  const onShowCallback = () => {
    trackCta({
      click_name: 'stats_lpi_card_tooltip',
      type: 'button',
      text: '',
    })
  }

  const tooltip = (
    <div className='flex flex-col gap-6 text-body-text-3'>
      <p>{t('description.p')}</p>
      <p>{t('description.p2')}</p>
    </div>
  )

  const tooltipFooter = (
    <div className='flex flex-col gap-2 text-body-text-3'>
      <p className='flex flex-row items-center gap-3 font-semibold'>
        <Clock title='t' className='w-5 h-5 text-text-interactive' />
        {t('comingSoon.title')}
      </p>
      <p>{t('comingSoon.l1')}</p>
    </div>
  )

  return (
    <StatsCardInfoTooltip
      title={t('description.title')}
      tooltip={tooltip}
      tooltipFooter={tooltipFooter}
      onShowCallback={onShowCallback}
    />
  )
}

const MoreInStats = ({ kind = 'primary-bw' }: { kind?: 'primary-bw' | 'tertiary' }) => {
  const ct = useCommonTx('common')

  const isPrimaryBw = kind === 'primary-bw'
  const isTertiary = kind === 'tertiary'

  return (
    <>
      <Link
        href='/stats'
        passHref={true}
        className={clsx(
          'flex flex-row items-center gap-1 text-icon-subdued text-caption-1',
          isPrimaryBw && 'px-4 py-2 bg-neutral-warm-95 rounded-full hover:bg-neutral-warm-90',
          isTertiary && 'hover:underline',
        )}
      >
        <span>{ct('moreInStats')}</span>
        <ArrowRight title='t' className='w-5 h-5' />
      </Link>
    </>
  )
}

const LpiCounter = () => {
  const ct = useCommonTx('common')
  const [visible, setVisible] = useState(false)

  const { loading, error, overallLpiText, isOverallLpiAvailable } = useLpiSummary()

  const popover = {
    show: () => setVisible(true),
    hide: () => setVisible(false),
  }

  return (
    <QueryLoader
      loading={loading}
      error={error}
      data={!loading}
      LoadingComponent={<SkeletonLoader className='w-[90px] h-[40px]' />}
    >
      {visible && (
        <SliderResponsive onClose={popover.hide}>
          <div className='flex flex-col w-full p-8 relative h-full'>
            <Button kind='icon' size='none' className='absolute top-5 right-5' onClick={popover.hide}>
              <CloseIcon title='x' className='w-8 h-8' />
            </Button>
            <div className='flex flex-row items-center gap-2 mt-6'>
              <div className='w-[44px] h-[44px] flex items-center justify-center bg-neutral-cool-98 rounded-full'>
                <ThunderIcon className='w-5 h-8' />
              </div>
              <div className='text-heading-2 font-normal'>{ct('currentLpi')}</div>
            </div>
            <div className='flex flex-col items-center mt-8 mb-10'>
              <LpiStatCard layoutName='C' />
              <div className='absolute bottom-8 right-8 left-8 flex justify-end'>
                <MoreInStats kind='primary-bw' />
              </div>
            </div>
          </div>
        </SliderResponsive>
      )}

      <ButtonCounter onClick={visible ? popover.hide : popover.show}>
        <ThunderIcon className='w-[16px] h-[24px]' />
        {isOverallLpiAvailable ? (
          <div className='text-subheading-2 text-text-default'>{overallLpiText}</div>
        ) : (
          <div className='text-subheading-2 text-text-disabled'>--</div>
        )}
      </ButtonCounter>
    </QueryLoader>
  )
}

export const LpiCounterForPages = ({ className }: { className?: string }) => {
  return (
    <div className={clsx('flex lg:hidden', className)}>
      <LpiCounter />
    </div>
  )
}
